<template>
  <div class="offcanvas-body px-0">
    <img src="@/assets/img/registration_fee_cover.svg" class="card-img mb-4" />
    <LoadingContainer v-if="isLoading" />
    <template v-else>
      <div class="px-4">
        <p class="text-header">Escolha seu plano</p>
        <ScrollHorizontal :items="plans">
          <template v-slot:default="{ item }">
            <div
              :class="{
                'border-primary': selectedPlan?.id === item.id,
              }"
              class="border border-2 border-muted p-3 rounded-4 mb-3 cursor-pointer plan-card"
              @click="handleSelectPlan(item)"
            >
              <img :src="item.icon" alt="" class="mb-2" height="20" />
              <p class="text-subtitle mb-2">{{ item.title }}</p>
              <p class="text-paragraph d-flex">
                {{ item.description_heading }}
              </p>
            </div>
          </template>
        </ScrollHorizontal>

        <p class="text-paragraph">
          Voltado para quem busca recursos avançados. Oferece maior visibilidade
          nas buscas, ferramentas de gestão, selo de destaque no perfil, entre
          outros.
        </p>
      </div>

      <hr />

      <div class="px-4">
        <p class="text-header">Período</p>
        <ScrollHorizontal :items="selectedPlan?.prices">
          <template v-slot:default="{ item }">
            <div
              :class="{
                'border-primary': selectedPrice?.id === item.id,
              }"
              class="border border-2 border-muted p-3 rounded-4 my-3 cursor-pointer plan-card position-relative"
              @click="handleSelectPrice(item)"
            >
              <span
                class="badge bg-secondary position-absolute plan-card-badge"
                >{{ item.discount }}</span
              >
              <p class="text-subtitle my-2">{{ item.title }}</p>
              <p class="text-paragraph mb-2">
                {{ item.value }}
              </p>
              <p class="text-paragraph text-muted">
                {{ item.description }}
              </p>
            </div>
          </template>
        </ScrollHorizontal>
      </div>

      <hr />
      <div class="px-4">
        <p class="text-header">Principais benefícios</p>
        <ul class="list-group list-group-flush mb-4">
          <li
            v-for="item in selectedPlan?.features"
            :key="item"
            class="list-group-item d-flex align-items-center"
          >
            <i class="fa-solid fa-circle-check text-primary me-2"></i>
            <p class="text-paragraph mb-0">{{ item }}</p>
          </li>
        </ul>

        <p class="text-header">Método de pagamento</p>

        <div
          @click="handleCreditCard"
          class="border rounded-4 py-3 px-4 d-flex justify-content-between align-items-center cursor-pointer mb-4"
        >
          <p class="text-subtitle mb-0">
            {{
              session?.favorite_card
                ? `**** ${session.favorite_card.last_digits}`
                : "Adicionar cartão"
            }}
          </p>

          <div class="d-flex align-items-center">
            <p class="text-paragraph mb-0 me-3" v-if="session?.favorite_card">
              Alterar
            </p>

            <i class="fa-solid fa-chevron-right text-primary"></i>
          </div>
        </div>

        <p class="text-paragraph cursor-pointer" @click="handleTerms">
          Clicando em assinar, você estará concordando com nossos
          <span class="text-subtitle">termos de uso.</span>
        </p>

        <p class="text-label text-danger text-center">{{ errorMessage }}</p>

        <Button
          fullWidth
          :loading="isPending"
          @onClick="onSubscribe"
          :label="`Assinar por ${selectedPrice?.value}`"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { Plan, PlanPrice, SubsRequest } from "@/@types";
import LoadingContainer from "@/components/LoadingContainer.vue";
import router from "@/router";
import paths from "@/router/paths";
import { subsService } from "@/services";
import { useMainStore } from "@/store";
import { syncSessionUseCase } from "@/useCases";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import {
  closeOffCanvas,
  EOffcanvasViews,
  navigateCanvas,
  OffcanvasViewKey,
} from "@/views/OffcanvasViews/OffcanvasViewsConfig";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

const {
  session,
  subscription_info,
  clearSubscriptionInfo,
  saveSubscriptionInfo,
} = useMainStore();

const errorMessage = ref<string>();

const {
  data: plans,
  isLoading,
  error,
  isSuccess,
} = useQuery({
  queryFn: () => subsService.listPlans(),
  queryKey: ["plans"],
});

const selectedPlan = ref<Plan>();
const selectedPrice = ref<PlanPrice>();

onMounted(() => {
  setupPlan();
});

watch(isSuccess, () => setupPlan());

watch(error, () => {
  if (error) errorMessage.value = error.value?.message;
});

const setupPlan = async () => {
  if (plans.value && plans.value.length) {
    selectedPlan.value = subscription_info?.plan || plans.value[0];
    selectedPrice.value = subscription_info?.price || plans.value[0].prices[0];
  }
};

const route = useRoute();

const { isPending, mutateAsync: handleSubscribe } = useMutation({
  mutationFn: (params: SubsRequest) => subsService.subscribe(params),
  onSuccess: async ({ data }) => {
    await syncSessionUseCase.execute();

    if (data?.status === "past_due" || data?.status === "incomplete") {
      alert(
        "Ocorreu um problema ao realizar o pagamento. Por favor, verifique suas informações e tente novamente.",
      );
    } else {
      EventBus.$emit(EventBusNames.SHOW_TOAST, {
        title: "Assinatura realizada",
        msg: `Bem vindo(a) ao ${selectedPlan.value?.title}, você já está usufruindo dos benefícios.`,
      });
    }

    EventBus.$emit(EventBusNames.ON_HOLDING_UPDATE);
    EventBus.$emit(EventBusNames.REFRESH_MY_PLAN);

    clearSubscriptionInfo();
    closeOffCanvas();

    if (route.path === paths.onHolding) {
      router.push(paths.home);
    }
  },
  onError: (error) => {
    errorMessage.value = error.message;
  },
});

const onSubscribe = async () => {
  try {
    if (!selectedPlan.value) {
      errorMessage.value = "Selecione um plano";
      return;
    }
    if (!selectedPrice.value) {
      errorMessage.value = "Selecione um período";
      return;
    }
    // if (
    //   session &&
    //   session.subscription &&
    //   session.subscription.subscription_plan_id === selectedPlan.value.id
    // ) {
    //   errorMessage.value = "Você já é assinante do plano selecionado";
    //   return;
    // }
    if (!session || !session.favorite_card) {
      errorMessage.value = "Selecione um método de pagamento";
      return;
    }

    errorMessage.value = "";

    const params: SubsRequest = {
      subscription_plan_id: selectedPlan.value.id,
      subscription_plan_price_id: selectedPrice.value.id,
      card_id: session.favorite_card.card_id,
    };

    handleSubscribe(params);
  } catch (error: any) {
    console.error(error);
    errorMessage.value = error.message;
  }
};

const handleSelectPlan = (plan: Plan) => {
  selectedPlan.value = plan;
  selectedPrice.value = plan.prices[0];
  saveSubscriptionInfo({
    plan: plan,
    price: plan.prices[0],
  });
};

const handleSelectPrice = (price: PlanPrice) => {
  selectedPrice.value = price;
  saveSubscriptionInfo({
    plan: selectedPlan.value,
    price: price,
  });
};

const handleCreditCard = () => {
  EventBus.$emit(EventBusNames.GET_CREDIT_CARDS);
  if (session && session.favorite_card) {
    navigateCanvas(
      EOffcanvasViews.credit_cards as OffcanvasViewKey,
      null,
      false,
    );
  } else {
    navigateCanvas(
      EOffcanvasViews.add_credit_card as OffcanvasViewKey,
      null,
      false,
    );
  }
};

const handleTerms = () => {
  navigateCanvas(EOffcanvasViews.terms_of_use, null, false);
};
</script>

<style scoped>
.plan-card {
  white-space: break-spaces;
  max-width: 182px;
  min-height: 142px;
}
.plan-card-badge {
  top: -10px;
}
</style>
